import { useQuery } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DealStatusEnumType, SyndicateTypeEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CreateLayoutContent } from 'layouts/CreateLayout';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

const DEFAULT_DISCLOSURE =
  'Early-stage ventures are very risky, and you may lose all your money if the company is unable to achieve PMF or scale, and shuts down.';

const ADD_DEAL_ENGAGEMENT_RULES_MUTATION = gql(`
  mutation AddDealEngagementRules($dealId: ID!, $customEngagementRules: [String!]!) {
    addDealEngagementRules(dealId: $dealId, customEngagementRules: $customEngagementRules) {
      id
      customEngagementRules
    }
  }
`);

const GET_DEAL_ENGAGEMENT_RULES_QUERY = gql(`
  query GetDealEngagementRules($dealId: ID!) {
    deal(id: $dealId) {
      id
      customEngagementRules
    }
  }
`);

const DISCLOSURES_QUERY = gql(`
  query DealDisclosures($id: ID!) {
    deal(id: $id) {
      id
      status
      dealDisclosures {
        id
        risksNote
        hasLeadInvestedInCompanyBefore
        doesLeadHaveAdvisoryShares
        isLeadEmployeeAtCompany
        doesLeadHaveRelativesInCompany
        hasSameTermsForCoInvestors
        isLeadInvestingOnSameTerms
        isLeadInvestingFromFund
        otherDisclosuresNote
      }
      syndicate {
        id
        type
      }
    }
  }
`);

const UPDATE_DEAL_DISCLOSURES_MUTATION = gql(`
  mutation UpdateDealDisclosures(
    $dealId: ID!
    $risksNote: String
    $hasLeadInvestedInCompanyBefore: Boolean!
    $doesLeadHaveAdvisoryShares: Boolean!
    $isLeadEmployeeAtCompany: Boolean!
    $doesLeadHaveRelativesInCompany: Boolean!
    $hasSameTermsForCoInvestors: Boolean!
    $isLeadInvestingOnSameTerms: Boolean!
    $isLeadInvestingFromFund: Boolean!
    $otherDisclosuresNote: String
  ) {
    updateDealDisclosures(
      dealId: $dealId
      risksNote: $risksNote
      hasLeadInvestedInCompanyBefore: $hasLeadInvestedInCompanyBefore
      doesLeadHaveAdvisoryShares: $doesLeadHaveAdvisoryShares
      isLeadEmployeeAtCompany: $isLeadEmployeeAtCompany
      doesLeadHaveRelativesInCompany: $doesLeadHaveRelativesInCompany
      hasSameTermsForCoInvestors: $hasSameTermsForCoInvestors
      isLeadInvestingOnSameTerms: $isLeadInvestingOnSameTerms
      isLeadInvestingFromFund: $isLeadInvestingFromFund
      otherDisclosuresNote: $otherDisclosuresNote
    ) {
      id
      dealDisclosures {
        id
        risksNote
        hasLeadInvestedInCompanyBefore
        doesLeadHaveAdvisoryShares
        isLeadEmployeeAtCompany
        doesLeadHaveRelativesInCompany
        hasSameTermsForCoInvestors
        isLeadInvestingOnSameTerms
        isLeadInvestingFromFund
        otherDisclosuresNote
      }
    }
  }
`);

const ENGAGEMENT_RULE_OTHERS_VALUE = 'others';

const RisksDisclosures: FC = () => {
  const navigate = useNavigate();
  const { showToast, handleToastOpen, handleToastClose } = useToast();

  const { syndicateId, dealId } = useParams<{ syndicateId: string; dealId: string }>() as {
    syndicateId: string;
    dealId: string;
  };

  const { loading, error, data, refetch } = useQuery(DISCLOSURES_QUERY, {
    variables: {
      id: dealId,
    },
  });

  const { data: engagementRulesData } = useQuery(GET_DEAL_ENGAGEMENT_RULES_QUERY, {
    variables: {
      dealId,
    },
  });

  const [updateDealDisclosures, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_DEAL_DISCLOSURES_MUTATION
  );

  const [
    addDealEngagementRules,
    { loading: _addDealEngagementRulesLoading, error: _addDealEngagementRulesError },
  ] = useMutation(ADD_DEAL_ENGAGEMENT_RULES_MUTATION);

  if (loading) return <LoadingIndicator />;

  if (error || !data || !dealId) return <ErrorMessage error={error} refetch={refetch} />;
  const isRuv = data.deal.syndicate.type === SyndicateTypeEnumType.Ruv;
  const isDealUnderReview = data.deal.status === DealStatusEnumType.UnderOpsReview;

  const defaultEngagementRules = engagementRulesData?.deal?.customEngagementRules || [];
  const defaultEnableCustomEngagementRules = defaultEngagementRules.length > 0 ? true : false;

  return (
    <CreateLayoutContent
      title="What should others know?"
      subTitle="Describe the risks and other disclaimers in that"
    >
      <FormPanel
        loading={updateLoading}
        error={updateError}
        onSubmit={data => {
          Promise.all([
            addDealEngagementRules({
              variables: {
                dealId,
                customEngagementRules: data?.enableCustomEngagementRules
                  ? data.customEngagementRules
                  : [],
              },
            }),
            updateDealDisclosures({
              variables: {
                dealId,
                risksNote: data.risksNote,
                hasLeadInvestedInCompanyBefore: data.hasLeadInvestedInCompanyBefore || false,
                doesLeadHaveAdvisoryShares: data.doesLeadHaveAdvisoryShares || false,
                isLeadEmployeeAtCompany: data.isLeadEmployeeAtCompany || false,
                doesLeadHaveRelativesInCompany: data.doesLeadHaveRelativesInCompany || false,
                hasSameTermsForCoInvestors: data.hasSameTermsForCoInvestors || false,
                isLeadInvestingOnSameTerms: data.isLeadInvestingOnSameTerms || false,
                isLeadInvestingFromFund: data.isLeadInvestingFromFund || false,
                otherDisclosuresNote: data.otherPotentialConflicts
                  ? data.otherDisclosures
                  : undefined,
              },
            }),
          ]).then(() => {
            if (isDealUnderReview) {
              navigate(`/syndicate-dashboard/${syndicateId}/deals/create/${dealId}/under-review`);
              return;
            }
            if (isRuv) {
              navigate(`/syndicate-dashboard/${syndicateId}/deals/create/${dealId}/e-sign-submit`);
              return;
            } else {
              navigate(
                `/syndicate-dashboard/${syndicateId}/deals/create/${dealId}/carry-recipients`
              );
            }
          });
        }}
        onCancel={() => {
          navigate(`/syndicate-dashboard/${syndicateId}/deals/create/${dealId}/memo`);
        }}
        submitButtonLabel="Save and continue"
        cancelButtonLabel="Back"
      >
        <FormInput
          fieldName="risksNote"
          type="textarea"
          fullWidth
          defaultValue={data.deal.dealDisclosures?.risksNote || (isRuv ? DEFAULT_DISCLOSURE : '')}
          helperText="Disclosing specific risk factors is required for compliance"
          label=""
          validators={{
            required: true,
          }}
        />

        {/* NO ADDITIONAL DISCLOSURES NEEDED FOR RUV */}
        {isRuv ? (
          <></>
        ) : (
          <>
            <FormInput
              type="switch"
              fieldName="hasLeadInvestedInCompanyBefore"
              label="I invested in a previous round"
              defaultValue={data.deal.dealDisclosures?.hasLeadInvestedInCompanyBefore || false}
            />
            <FormInput
              type="switch"
              fieldName="doesLeadHaveAdvisoryShares"
              label="I have advisory shares, warrants, or other shares"
              defaultValue={data.deal.dealDisclosures?.doesLeadHaveAdvisoryShares || false}
            />
            <FormInput
              type="switch"
              fieldName="isLeadEmployeeAtCompany"
              label="I am an officer or employee of the company"
              defaultValue={data.deal.dealDisclosures?.isLeadEmployeeAtCompany || false}
            />
            <FormInput
              type="switch"
              fieldName="doesLeadHaveRelativesInCompany"
              label="I have a relative or significant other working at the company"
              defaultValue={data.deal.dealDisclosures?.doesLeadHaveRelativesInCompany || false}
            />
            <FormInput
              type="switch"
              fieldName="hasSameTermsForCoInvestors"
              label="I have the same terms as other co-investors"
              defaultValue={data.deal.dealDisclosures?.hasSameTermsForCoInvestors || false}
            />
            <FormInput
              type="switch"
              fieldName="isLeadInvestingOnSameTerms"
              label="I am investing on the same terms as other investors"
              defaultValue={data.deal.dealDisclosures?.isLeadInvestingOnSameTerms || false}
            />
            <FormInput
              type="switch"
              fieldName="isLeadInvestingFromFund"
              label="I am investing from a fund"
              defaultValue={data.deal.dealDisclosures?.isLeadInvestingFromFund || false}
            />
            <FormInput
              type="switch"
              fieldName="otherPotentialConflicts"
              label="I have other potential conflicts to disclose"
              defaultValue={data.deal.dealDisclosures?.otherDisclosuresNote ? true : false}
            />
            <FormInput
              type="textarea"
              fullWidth
              fieldName="otherDisclosures"
              label="Other Disclosures"
              defaultValue={data.deal.dealDisclosures?.otherDisclosuresNote || ''}
              conditionsToShow={{
                matches: [
                  {
                    field: 'otherPotentialConflicts',
                    condition: '===',
                    value: 'true',
                  },
                ],
              }}
            />
          </>
        )}

        <div className="col-span-full mt-4">
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <FormInput
              type="switch"
              fieldName="enableCustomEngagementRules"
              label="Disclose deal-specific restrictions (if any)"
              helperText="Select conditions to determine how this investment opportunity is accessed"
              defaultValue={defaultEnableCustomEngagementRules}
            />
            <FormInput
              type="filter-multi-select"
              fieldName="customEngagementRules"
              label="Select condition"
              placeholder="Select"
              defaultValue={defaultEngagementRules}
              options={[
                {
                  value:
                    'I/We am/are not presently employed at or have an existing stake in a direct or closely competing business',
                  label:
                    'Investors must not be currently employed at or having an existing stake in a direct or closely competing business',
                },
                {
                  value:
                    'I/We confirm that we have previously invested in or have worked in/with companies in the same sector',
                  label:
                    'Limit participation to investors with prior or current sector-specific/domain experience or exposure',
                },
                { value: ENGAGEMENT_RULE_OTHERS_VALUE, label: 'Others' },
              ]}
              conditionsToShow={{
                matches: [
                  {
                    field: 'enableCustomEngagementRules',
                    condition: '===',
                    value: 'true',
                  },
                ],
              }}
              onOthersPick={handleToastOpen}
            />
          </div>
        </div>
      </FormPanel>
      <Toast
        title="Notice"
        message="Please contact your deal manager at hi@angellistindia.com"
        show={showToast}
        handleClose={handleToastClose}
        type="error"
        position="bottom-right"
      />
    </CreateLayoutContent>
  );
};

export default RisksDisclosures;
